import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';
import { Users, UserPlus, Edit, Trash2, Check, X, Clock, UserCheck, UserX, Search, RefreshCw, Mail } from 'lucide-react';
import OfferRelease from './OfferRelease';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HiringPortal = ({ user }) => {
  const [activeTab, setActiveTab] = useState('recruitment');
  const [candidates, setCandidates] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterResult, setFilterResult] = useState('all');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingCandidate, setEditingCandidate] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    contact_number: '',
    aadhar_number: '',
    date: new Date().toISOString().split('T')[0],
    gender: 'Male',
    role: '',
    level: '',
    interview_date: '',
    interview_result: '',
    feedback: '',
    date_of_joining: ''
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const [candidatesRes, dashboardRes] = await Promise.all([
        axios.get(`${API}/recruitment/candidates`, config),
        axios.get(`${API}/recruitment/dashboard`, config)
      ]);
      
      setCandidates(candidatesRes.data);
      setDashboard(dashboardRes.data);
    } catch (error) {
      toast.error('Failed to load recruitment data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      contact_number: '',
      aadhar_number: '',
      date: new Date().toISOString().split('T')[0],
      gender: 'Male',
      role: '',
      level: '',
      interview_date: '',
      interview_result: '',
      feedback: '',
      date_of_joining: ''
    });
    setEditingCandidate(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      if (editingCandidate) {
        await axios.put(`${API}/recruitment/candidates/${editingCandidate.id}`, formData, config);
        toast.success('Candidate updated successfully');
      } else {
        await axios.post(`${API}/recruitment/candidates`, formData, config);
        toast.success('Candidate added successfully');
      }
      
      setShowAddDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(editingCandidate ? 'Failed to update candidate' : 'Failed to add candidate');
    }
  };

  const handleEdit = (candidate) => {
    setEditingCandidate(candidate);
    setFormData({
      name: candidate.name || '',
      email: candidate.email || '',
      contact_number: candidate.contact_number || '',
      aadhar_number: candidate.aadhar_number || '',
      date: candidate.date || '',
      gender: candidate.gender || 'Male',
      role: candidate.role || '',
      level: candidate.level || '',
      interview_date: candidate.interview_date || '',
      interview_result: candidate.interview_result || '',
      feedback: candidate.feedback || '',
      date_of_joining: candidate.date_of_joining || ''
    });
    setShowAddDialog(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this candidate?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/recruitment/candidates/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Candidate deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete candidate');
    }
  };

  const handleInlineUpdate = async (candidateId, field, value) => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      await axios.put(`${API}/recruitment/candidates/${candidateId}`, { [field]: value }, config);
      toast.success('Updated successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const filteredCandidates = candidates.filter(c => {
    const matchesSearch = c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         c.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         c.role?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         c.contact_number?.includes(searchTerm);
    const matchesFilter = filterResult === 'all' || c.interview_result === filterResult || 
                         (filterResult === 'Pending' && !c.interview_result);
    return matchesSearch && matchesFilter;
  });

  const getResultBadge = (result) => {
    switch (result) {
      case 'Selected':
        return <Badge className="bg-green-500 hover:bg-green-600"><Check className="w-3 h-3 mr-1" />Selected</Badge>;
      case 'Rejected':
        return <Badge className="bg-red-500 hover:bg-red-600"><X className="w-3 h-3 mr-1" />Rejected</Badge>;
      case 'On Hold':
        return <Badge className="bg-yellow-500 hover:bg-yellow-600"><Clock className="w-3 h-3 mr-1" />On Hold</Badge>;
      default:
        return <Badge variant="secondary">Pending</Badge>;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="space-y-6" data-testid="hiring-portal">
      {/* Header */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Users className="w-6 h-6 text-indigo-600" />
              <div>
                <CardTitle>Hiring Portal</CardTitle>
                <CardDescription>Manage recruitment candidates, interviews, and offer releases</CardDescription>
              </div>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2 max-w-md">
          <TabsTrigger value="recruitment" className="flex items-center gap-2" data-testid="tab-recruitment">
            <Users className="w-4 h-4" />
            Recruitment
          </TabsTrigger>
          <TabsTrigger value="offer-release" className="flex items-center gap-2" data-testid="tab-offer-release">
            <Mail className="w-4 h-4" />
            Offer Release
          </TabsTrigger>
        </TabsList>

        {/* Recruitment Tab */}
        <TabsContent value="recruitment" className="space-y-6">
          {/* Dashboard Stats */}
          {dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4" data-testid="recruitment-dashboard">
          {/* Total */}
          <Card className="bg-gradient-to-br from-indigo-50 to-indigo-100 border-indigo-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Users className="w-8 h-8 text-indigo-600" />
                <div>
                  <p className="text-xs text-indigo-600 font-medium">Total</p>
                  <p className="text-2xl font-bold text-indigo-700">{dashboard.total_candidates}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Male */}
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white font-bold">M</div>
                <div>
                  <p className="text-xs text-blue-600 font-medium">Male</p>
                  <p className="text-2xl font-bold text-blue-700">{dashboard.gender_stats?.Male || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Female */}
          <Card className="bg-gradient-to-br from-pink-50 to-pink-100 border-pink-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-pink-500 flex items-center justify-center text-white font-bold">F</div>
                <div>
                  <p className="text-xs text-pink-600 font-medium">Female</p>
                  <p className="text-2xl font-bold text-pink-700">{dashboard.gender_stats?.Female || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Selected */}
          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <UserCheck className="w-8 h-8 text-green-600" />
                <div>
                  <p className="text-xs text-green-600 font-medium">Selected</p>
                  <p className="text-2xl font-bold text-green-700">{dashboard.result_stats?.Selected || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Rejected */}
          <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <UserX className="w-8 h-8 text-red-600" />
                <div>
                  <p className="text-xs text-red-600 font-medium">Rejected</p>
                  <p className="text-2xl font-bold text-red-700">{dashboard.result_stats?.Rejected || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* On Hold */}
          <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Clock className="w-8 h-8 text-yellow-600" />
                <div>
                  <p className="text-xs text-yellow-600 font-medium">On Hold</p>
                  <p className="text-2xl font-bold text-yellow-700">{dashboard.result_stats?.['On Hold'] || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Candidates Table */}
      <Card data-testid="candidates-table-card">
        <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-4">
          <CardTitle className="text-lg">Recruitment Candidates</CardTitle>
          <div className="flex flex-wrap items-center gap-3">
            {/* Search */}
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search by name, email, role..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-candidates"
              />
            </div>
            
            {/* Filter by Result */}
            <Select value={filterResult} onValueChange={setFilterResult}>
              <SelectTrigger className="w-40" data-testid="filter-result">
                <SelectValue placeholder="Filter by result" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Results</SelectItem>
                <SelectItem value="Selected">Selected</SelectItem>
                <SelectItem value="Rejected">Rejected</SelectItem>
                <SelectItem value="On Hold">On Hold</SelectItem>
                <SelectItem value="Pending">Pending</SelectItem>
              </SelectContent>
            </Select>
            
            {/* Refresh Button */}
            <Button onClick={fetchData} variant="outline" size="sm">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            
            {/* Add Candidate Button */}
            <Dialog open={showAddDialog} onOpenChange={(open) => {
              setShowAddDialog(open);
              if (!open) resetForm();
            }}>
              <DialogTrigger asChild>
                <Button className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-candidate-btn">
                  <UserPlus className="w-4 h-4 mr-2" />
                  Add Candidate
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingCandidate ? 'Edit Candidate' : 'Add New Candidate'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4" data-testid="candidate-form">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Name *</Label>
                      <Input
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        data-testid="input-name"
                      />
                    </div>
                    <div>
                      <Label>Email</Label>
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="candidate@example.com"
                        data-testid="input-email"
                      />
                    </div>
                    <div>
                      <Label>Contact Number *</Label>
                      <Input
                        value={formData.contact_number}
                        onChange={(e) => setFormData({ ...formData, contact_number: e.target.value })}
                        required
                        data-testid="input-contact"
                      />
                    </div>
                    <div>
                      <Label>Aadhar Number</Label>
                      <Input
                        value={formData.aadhar_number}
                        onChange={(e) => setFormData({ ...formData, aadhar_number: e.target.value })}
                        data-testid="input-aadhar"
                      />
                    </div>
                    <div>
                      <Label>Date *</Label>
                      <Input
                        type="date"
                        value={formData.date}
                        onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                        required
                        data-testid="input-date"
                      />
                    </div>
                    <div>
                      <Label>Gender *</Label>
                      <Select value={formData.gender} onValueChange={(v) => setFormData({ ...formData, gender: v })}>
                        <SelectTrigger data-testid="select-gender">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Male">Male</SelectItem>
                          <SelectItem value="Female">Female</SelectItem>
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Role/Position *</Label>
                      <Input
                        value={formData.role}
                        onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                        required
                        placeholder="e.g., Software Engineer"
                        data-testid="input-role"
                      />
                    </div>
                    <div>
                      <Label>Level</Label>
                      <Select value={formData.level || ''} onValueChange={(v) => setFormData({ ...formData, level: v })}>
                        <SelectTrigger data-testid="select-level">
                          <SelectValue placeholder="Select level" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="L1">L1</SelectItem>
                          <SelectItem value="L1.5">L1.5</SelectItem>
                          <SelectItem value="L2">L2</SelectItem>
                          <SelectItem value="L2.5">L2.5</SelectItem>
                          <SelectItem value="L3">L3</SelectItem>
                          <SelectItem value="L3.5">L3.5</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Interview Date</Label>
                      <Input
                        type="date"
                        value={formData.interview_date}
                        onChange={(e) => setFormData({ ...formData, interview_date: e.target.value })}
                        data-testid="input-interview-date"
                      />
                    </div>
                    <div>
                      <Label>Interview Result</Label>
                      <Select value={formData.interview_result || ''} onValueChange={(v) => setFormData({ ...formData, interview_result: v })}>
                        <SelectTrigger data-testid="select-result">
                          <SelectValue placeholder="Select result" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Selected">Selected</SelectItem>
                          <SelectItem value="Rejected">Rejected</SelectItem>
                          <SelectItem value="On Hold">On Hold</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Date of Joining</Label>
                      <Input
                        type="date"
                        value={formData.date_of_joining}
                        onChange={(e) => setFormData({ ...formData, date_of_joining: e.target.value })}
                        data-testid="input-doj"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Feedback</Label>
                    <Textarea
                      value={formData.feedback}
                      onChange={(e) => setFormData({ ...formData, feedback: e.target.value })}
                      placeholder="Interview feedback and notes..."
                      rows={3}
                      data-testid="input-feedback"
                    />
                  </div>
                  <Button type="submit" className="w-full" data-testid="submit-candidate">
                    {editingCandidate ? 'Update Candidate' : 'Add Candidate'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center p-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="candidates-table">
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="font-bold">Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Aadhar</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Month</TableHead>
                    <TableHead>Gender</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Level</TableHead>
                    <TableHead>Interview Date</TableHead>
                    <TableHead>Result</TableHead>
                    <TableHead>Feedback</TableHead>
                    <TableHead>DOJ</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCandidates.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={14} className="text-center py-8 text-gray-500">
                        No candidates found. Click "Add Candidate" to add one.
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredCandidates.map((candidate) => (
                      <TableRow key={candidate.id} data-testid={`candidate-row-${candidate.id}`}>
                        <TableCell className="font-medium">{candidate.name}</TableCell>
                        <TableCell className="text-sm">{candidate.email || '-'}</TableCell>
                        <TableCell>{candidate.contact_number}</TableCell>
                        <TableCell className="text-sm">{candidate.aadhar_number || '-'}</TableCell>
                        <TableCell>{formatDate(candidate.date)}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{candidate.month || '-'}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={candidate.gender === 'Male' ? 'default' : candidate.gender === 'Female' ? 'secondary' : 'outline'}>
                            {candidate.gender}
                          </Badge>
                        </TableCell>
                        <TableCell>{candidate.role}</TableCell>
                        <TableCell>{candidate.level || '-'}</TableCell>
                        <TableCell>{formatDate(candidate.interview_date)}</TableCell>
                        <TableCell>
                          <Select
                            value={candidate.interview_result || ''}
                            onValueChange={(v) => handleInlineUpdate(candidate.id, 'interview_result', v)}
                          >
                            <SelectTrigger className="w-32 h-8" data-testid={`result-select-${candidate.id}`}>
                              <SelectValue>{getResultBadge(candidate.interview_result)}</SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Selected">Selected</SelectItem>
                              <SelectItem value="Rejected">Rejected</SelectItem>
                              <SelectItem value="On Hold">On Hold</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell className="max-w-[150px]">
                          <span className="truncate block" title={candidate.feedback}>
                            {candidate.feedback || '-'}
                          </span>
                        </TableCell>
                        <TableCell>{formatDate(candidate.date_of_joining)}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(candidate)}
                              className="text-blue-600 hover:text-blue-700"
                              data-testid={`edit-candidate-${candidate.id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            {(user?.role === 'admin' || user?.role === 'superadmin') && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(candidate.id)}
                                className="text-red-600 hover:text-red-700"
                                data-testid={`delete-candidate-${candidate.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          )}
          <div className="mt-4 text-sm text-gray-500">
            Showing {filteredCandidates.length} of {candidates.length} candidates
          </div>
        </CardContent>
      </Card>
        </TabsContent>

        {/* Offer Release Tab */}
        <TabsContent value="offer-release">
          <OfferRelease user={user} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default HiringPortal;
